import { z } from 'zod';
// ---------------------------------------------------------------------------
// Patient todo-list card print (Domain 7 — Document & PDF Engine, issue #31
// B2; REQ-DOC-017 on Domain 3/4 participation data)
// ---------------------------------------------------------------------------
/** `HH:MM`, the shape the cockpit stores and `PatientVisitDto` carries. */
const VisitTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
/**
 * Body of `POST /api/v1/documents/patient-todo/generate` and of the
 * render-pdf counterpart (REQ-DOC-017).
 *
 * ONE identifier — the patient visit, the same single-id contract as VCL
 * (`GenerateVclRequestSchema`) and the destruction protocol (Q-DOC-001
 * convention). Everything else (patient display data, open tasks, upcoming
 * appointments) is resolved by the composition service through the exported
 * read doors of the D3/D4 clusters — no cross-cluster SQL.
 */
export const GeneratePatientTodoRequestSchema = z.object({
    patientVisitId: z.number().int().positive(),
});
/**
 * The A4 header of the card (REQ-DOC-017; accepted wireframe
 * `ui/patient-todo-list.ui.md`). Values are display-resolved — the template
 * never touches ids (VCL header convention). NO PESEL in any shape, masked or
 * not: the card is handed to the patient, stricter than the VCL header
 * (feature acceptance decision, 2026-10-19 — the field is simply absent).
 */
export const PatientTodoHeaderSchema = z.object({
    firstName: z.string().min(1).max(255),
    lastName: z.string().min(1).max(255),
    screeningNumber: z.string().min(1).max(64),
    /** Trial protocol identifier (e.g. `EFC18325`). */
    protocolNumber: z.string().min(1),
    /** Number of the site running the visit (e.g. `DEMO-Północ`). */
    siteNumber: z.string().min(1),
    /** Display name of the visit, composed by the service (`Name 1 (Name 2)` convention). */
    visitName: z.string().min(1).max(255),
    /** Date of the triggering Day Plan visit (`dateReal ?? dateScheduled`). */
    visitDate: z.iso.date(),
});
/**
 * One printed row of the tasks table (REQ-DOC-017): one OPEN participation
 * task — the same D4 source as the OPEN TASKS section of the VCL, so the
 * shape mirrors `VclOpenTaskSchema` field for field.
 */
export const PatientTodoTaskSchema = z.object({
    taskId: z.number().int().positive(),
    name: z.string().min(1),
    /** Optional notes; null → the template prints the cell empty. */
    comments: z.string().nullable(),
    startDate: z.iso.date(),
});
/**
 * One printed row of the appointments table (REQ-DOC-017): a visit scheduled
 * after the triggering one, through the end of participation — no day
 * threshold cuts the list (frozen feature: the far visit V6 must appear).
 */
export const PatientTodoAppointmentSchema = z.object({
    /** Display name of the scheduled visit. */
    visitName: z.string().min(1).max(255),
    visitDate: z.iso.date(),
    /** `HH:MM`; null when the booking carries no time — printed empty. */
    visitTime: VisitTimeSchema.nullable(),
});
/**
 * The composed todo-list card returned by the generation endpoint and carried
 * as the render-job payload — everything the A4 template renders (mirrors
 * `KitDestructionDocumentDtoSchema`). The home-care section carries NO data:
 * the template reserves the handwritten box (Q-DOC-005 decision 2), and the
 * single staff-signature place is template chrome as well.
 *
 * Both lists may be empty — the card stays printable and the template shows
 * the honest "Brak otwartych zadań." / "Brak zaplanowanych wizyt." states.
 *
 * Sub-shapes are reachable through indexed access
 * (`PatientTodoDocumentDto['header']` …); dedicated sub-types are deliberately
 * NOT exported, mirroring the VCL convention.
 */
export const PatientTodoDocumentDtoSchema = z.object({
    patientVisitId: z.number().int().positive(),
    header: PatientTodoHeaderSchema,
    tasks: z.array(PatientTodoTaskSchema),
    appointments: z.array(PatientTodoAppointmentSchema),
});
//# sourceMappingURL=patient-todo.schema.js.map